import { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useConfig } from 'shared/config/ConfigContext';
import type { PaleState, ThunkDispatchFunc } from '@/interfaces';
import { actions as filterActions } from '@/state/filterActions';
import { fetchResults } from '@/state/resultActions';

export const useResultsNavigator = () => {
    const dispatch = useDispatch<ThunkDispatchFunc>();
    const { contextPathPP } = useConfig();

    const results = useSelector((s: PaleState) => s.results, shallowEqual);
    const filters = useSelector((s: PaleState) => s.filters, shallowEqual);

    const currentPage = results.currentPage ?? 1;
    const totalPages = results.totalPages ?? 1;

    const setCurrentPage = useCallback(
        (page?: number) => {
            dispatch(filterActions.changePage(page));
        },
        [dispatch],
    );

    const triggerFetch = useCallback(() => {
        dispatch(fetchResults());
    }, [dispatch]);

    const resetFilters = useCallback(() => {
        dispatch(filterActions.resetFilters());
    }, [dispatch]);

    const resetAndRefetch = useCallback(() => {
        dispatch(filterActions.resetFilters());
        dispatch(fetchResults());
    }, [dispatch]);

    const createHref = useCallback((p: number) => `${contextPathPP}/resultat?side=${p}`, [contextPathPP]);

    const handleChange = useCallback(
        (page: number) => {
            setCurrentPage(page === 1 ? undefined : page);

            if (typeof window !== 'undefined') {
                const url = new URL(window.location.href);
                url.searchParams.set('side', String(page));

                const nextUrl = url.toString();
                if (nextUrl !== window.location.href) {
                    window.history.pushState(null, '', nextUrl);
                }
            }

            triggerFetch();

            if (typeof window === 'undefined') return;

            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        },
        [setCurrentPage, triggerFetch],
    );

    return {
        results,
        filters,
        currentPage,
        totalPages,
        createHref,
        handleChange,
        resetFilters,
        resetAndRefetch,
    };
};
